/**
 * Centralized API-error → display-string resolver.
 *
 * NOTE: this exists because `err.response.data` is NOT always the same shape.
 * The API returns a bare string for some failures ("User already exists") and a
 * JSON object for others (`{ message }`, `{ error }`, `{ errors: [...] }`).
 * Callers that did `onError(err?.response?.data || ...)` therefore passed a raw
 * OBJECT into a `string` prop, which React refuses to render ("Objects are not
 * valid as a React child") and which otherwise shows as "[object Object]".
 *
 * This helper checks the type first and only then reads parameters, so every
 * toast gets a real sentence regardless of which shape the backend sent.
 */

/**
 * CHANGE: named constant in place of a bare `500` literal.
 *
 * NOTE: Axios exports `HttpStatusCode.InternalServerError`, which would be the
 * obvious reuse, but importing axios here is NOT viable: axios 1.x ships its
 * entry point as ESM and CRA's Jest does not transform `node_modules`, so the
 * import makes this module — and every test touching the seven files that
 * import it — fail with "Cannot use import statement outside a module".
 * A local constant keeps this utility dependency-free and testable.
 */
const HTTP_INTERNAL_SERVER_ERROR = 500;

/** Keys the API uses for the human-readable text, in the order we trust them. */
const MESSAGE_KEYS = ["message", "error", "details", "errorMessage", "detail", "title"];

/** Returns the trimmed string when `value` is a usable, non-empty string. */
const asNonEmptyString = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : null;
};

/**
 * Pulls text out of one validation-error entry. Spring field errors arrive as
 * `{ field, defaultMessage }`, so the field name is prefixed when present to
 * keep the toast actionable ("mobileNumber: must not be blank").
 */
const fromValidationEntry = (entry: unknown): string | null => {
  const direct = asNonEmptyString(entry);
  if (direct) return direct;

  if (!entry || typeof entry !== "object") return null;

  // NOTE: `unknown` values, not `any` — every read below goes through
  // `asNonEmptyString`, which type-checks before using the value.
  const item = entry as Record<string, unknown>;
  const text =
    asNonEmptyString(item.defaultMessage) ||
    asNonEmptyString(item.message) ||
    asNonEmptyString(item.error);
  if (!text) return null;

  const field = asNonEmptyString(item.field) || asNonEmptyString(item.fieldName);
  return field ? `${field}: ${text}` : text;
};

/**
 * Reads a message out of an object payload: the known text keys first, then any
 * validation-error collection. Returns null when nothing readable is found, so
 * the caller can fall through to `err.message` / the fallback.
 */
const fromObjectPayload = (payload: Record<string, unknown>): string | null => {
  for (const key of MESSAGE_KEYS) {
    const text = asNonEmptyString(payload[key]);
    if (text) return text;
  }

  // Validation failures: `{ errors: [...] }` / `{ fieldErrors: [...] }`.
  // Several entries are joined so the user sees every invalid field at once.
  const collection = payload.errors || payload.fieldErrors || payload.violations;
  if (Array.isArray(collection)) {
    const parts = collection.map(fromValidationEntry).filter(Boolean) as string[];
    if (parts.length > 0) return parts.join(", ");
  }

  return null;
};

export interface ApiErrorMessageOptions {
  /**
   * When true, a 500 returns `fallback` instead of whatever the server sent.
   * Used by the project forms: a 500 body is a stack trace / raw servlet page,
   * which must never reach a toast. Client errors (4xx) are still shown,
   * because those carry the actionable reason ("Project name already exists").
   */
  useFallbackOnServerError?: boolean;
}

/**
 * Resolves any thrown API error into a string safe to put in a toast.
 *
 * Accepts either an Axios error (`{ response: { data, status } }`) OR a bare
 * response object (`{ data, status }`) — some callers hand over a non-2xx
 * `res` from the `.then()` branch rather than a thrown error.
 *
 * Resolution order:
 *   1. `fallback` when the status is 500 and `useFallbackOnServerError` is set.
 *   2. the data payload when it is a plain string.
 *   3. the data payload's object parameters (message / error / details / validation list).
 *   4. `statusText`, then the Axios `error.message`.
 *   5. `fallback`.
 */
export const getApiErrorMessage = (
  error: unknown,
  fallback = "Something went wrong",
  options: ApiErrorMessageOptions = {},
): string => {
  const typedError = error as {
    response?: { data?: unknown; statusText?: string; status?: number };
    data?: unknown;
    statusText?: string;
    status?: number;
    message?: string;
  };

  // A thrown Axios error nests everything under `response`; a bare response
  // object carries the same keys at the top level.
  const status = typedError?.response?.status ?? typedError?.status;
  if (options.useFallbackOnServerError && status === HTTP_INTERNAL_SERVER_ERROR) {
    return fallback;
  }

  const data = typedError?.response?.data ?? typedError?.data;

  // 1. String payload — render it as-is.
  const asString = asNonEmptyString(data);
  if (asString) return asString;

  // 2. Object payload — read the known parameters.
  if (data && typeof data === "object") {
    const fromObject = fromObjectPayload(data as Record<string, unknown>);
    if (fromObject) return fromObject;
  }

  // 3. Transport-level text (network error, 500 with an empty body, …).
  return (
    asNonEmptyString(typedError?.response?.statusText) ||
    asNonEmptyString(typedError?.statusText) ||
    asNonEmptyString(typedError?.message) ||
    fallback
  );
};

export default getApiErrorMessage;
